import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import store from './redux/store';
import { getCurrentUser, logoutUser } from './redux/slices/authSlice';
import InactivityLogout from './components/InactivityLogout';
import { LanguageProvider } from './context/LanguageContext';

// Lazy load components for code splitting
import { lazy, Suspense } from 'react';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const Settings = lazy(() => import('./pages/Settings'));
const VisaApplications = lazy(() => import('./pages/VisaApplications'));
const VisaTypes = lazy(() => import('./pages/VisaTypes'));
const QuizManagement = lazy(() => import('./pages/QuizManagement'));
const CaseStatuses = lazy(() => import('./pages/CaseStatuses'));
const Cases = lazy(() => import('./pages/Cases'));
const CaseDetail = lazy(() => import('./pages/CaseDetail'));
const Tags = lazy(() => import('./pages/Tags'));
const DestinationCountries = lazy(() => import('./pages/DestinationCountries'));
const QuizBuilder = lazy(() => import('./pages/QuizBuilder'));
const QuizResponses = lazy(() => import('./pages/QuizResponses'));
const Resources = lazy(() => import('./pages/Resources'));
const DocumentTypes = lazy(() => import('./pages/DocumentTypes'));
const WizardSteps = lazy(() => import('./pages/WizardSteps'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-DELVANTE-bg">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-DELVANTE-accent"></div>
  </div>
);

// Protected Route component - now checks for admin role
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useSelector((state) => state.auth);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Check if user is authenticated and has admin role
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    // User is authenticated but not an admin - redirect to unauthorized page
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Unauthorized page component
const UnauthorizedPage = () => {
  const dispatch = useDispatch();

  const handleReturnToLogin = async () => {
    // Clear the session by logging out
    await dispatch(logoutUser());
    // Navigate to login page
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-DELVANTE-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-DELVANTE-white rounded-xl shadow-xl p-8 text-center border border-DELVANTE-separator">
        <div className="mx-auto h-16 w-16 bg-DELVANTE-accent/10 rounded-full flex items-center justify-center mb-4">
          <svg className="h-8 w-8 text-DELVANTE-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="font-display text-2xl font-semibold text-DELVANTE-text mb-2">Access Denied</h2>
        <p className="text-DELVATNE-text leading-relaxed mb-6">
          You do not have permission to access the admin dashboard.
          Only users with admin privileges can access this area.
        </p>
        <button
          onClick={handleReturnToLogin}
          className="btn-primary"
        >
          Return to Login
        </button>
      </div>
    </div>
  );
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useSelector((state) => state.auth);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Main App component
const AppContent = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Check if user is authenticated via httpOnly cookie
    dispatch(getCurrentUser());
  }, [dispatch]);

  return (
    <div className="App">
      <InactivityLogout />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          
          {/* Visa Management Routes */}
          <Route
            path="/visa-applications"
            element={
              <ProtectedRoute>
                <VisaApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/visa-types"
            element={
              <ProtectedRoute>
                <VisaTypes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tags"
            element={
              <ProtectedRoute>
                <Tags />
              </ProtectedRoute>
            }
          />
          <Route
            path="/destination-countries"
            element={
              <ProtectedRoute>
                <DestinationCountries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/case-statuses"
            element={
              <ProtectedRoute>
                <CaseStatuses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases"
            element={
              <ProtectedRoute>
                <Cases />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <ProtectedRoute>
                <CaseDetail />
              </ProtectedRoute>
            }
          />

          {/* Quiz Management Routes */}
          <Route
            path="/quiz-management"
            element={
              <ProtectedRoute>
                <QuizManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quiz-builder"
            element={
              <ProtectedRoute>
                <QuizBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quiz-builder/:id"
            element={
              <ProtectedRoute>
                <QuizBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quiz-responses/:id"
            element={
              <ProtectedRoute>
                <QuizResponses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quiz-responses"
            element={
              <ProtectedRoute>
                <QuizResponses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resources"
            element={
              <ProtectedRoute>
                <Resources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/document-types"
            element={
              <ProtectedRoute>
                <DocumentTypes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wizard-steps"
            element={
              <ProtectedRoute>
                <WizardSteps />
              </ProtectedRoute>
            }
          />

          {/* Unauthorized page */}
          <Route
            path="/unauthorized"
            element={<UnauthorizedPage />}
          />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#091828',
            color: '#ebdfcb',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#091828',
              secondary: '#ebdfcb',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#091828',
              secondary: '#ebdfcb',
            },
          },
        }}
      />
    </div>
  );
};

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Main App wrapper
const App = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <Router>
            <AppContent />
          </Router>
        </LanguageProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
